"use client";

import { useMemo } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useDriversQuery, useVehiclesQuery, useZonesQuery } from "@/lib/api/queries";
import { buildDriverNameMap } from "@/lib/fleet/driver-name";
import { isNotificationUnread, toParsedEvent } from "@/lib/api/notifications";
import { trackingFollowHref } from "@/lib/notifications/links";
import {
  describeVehicleEvent,
  vehicleEventSeverity,
  type VehicleEventContext,
} from "@/lib/notifications/vehicle-events";
import type { Notification, VehicleNotification } from "@/types/fleet";

/**
 * The lookups that turn an event's ids into names: vehicle label, zone name, driver name. Shared
 * by the toast listener and the notifications feed so both read the same cached lists.
 */
export function useVehicleEventContext(): VehicleEventContext {
  const { user } = useAuth();
  const { t } = useLanguage();
  const enabled = !!user;
  const vehiclesQuery = useVehiclesQuery({ enabled });
  const zonesQuery = useZonesQuery({ enabled });
  const driversQuery = useDriversQuery({ enabled });

  return useMemo<VehicleEventContext>(
    () => ({
      t,
      vehiclesById: new Map((vehiclesQuery.data ?? []).map((vehicle) => [vehicle.id, vehicle])),
      zoneNamesById: new Map((zonesQuery.data ?? []).map((zone) => [zone.id, zone.name])),
      driverNamesById: buildDriverNameMap(driversQuery.data ?? []),
    }),
    [t, vehiclesQuery.data, zonesQuery.data, driversQuery.data],
  );
}

/**
 * Upstream's stored events as notifications, newest first, in the same words, icons and
 * severities the toast uses. Read state comes from `seenAt` (`isNotificationUnread`).
 */
export function useStoredEventNotifications(
  items: readonly VehicleNotification[],
  seenAt: string,
): Notification[] {
  const context = useVehicleEventContext();

  return useMemo<Notification[]>(
    () =>
      items.map((item) => {
        const parsed = toParsedEvent(item);
        const { title, message, vehicleName } = describeVehicleEvent(item.vehicleId, parsed, context, {
          speed: item.speed ?? undefined,
          fallbackNames: {
            vehicle: item.detail.registration,
            zone: item.detail.zoneTitle,
            driver: item.detail.driverName,
          },
        });
        return {
          id: item.id,
          type: vehicleEventSeverity(parsed.type),
          kind: parsed.type,
          title,
          message,
          vehicleId: item.vehicleId,
          vehicleName,
          timestamp: item.occurredAt,
          read: !isNotificationUnread(item, seenAt),
          source: "vehicle" as const,
          href: trackingFollowHref(item.vehicleId),
          location: item.lat !== null && item.lon !== null ? { lat: item.lat, lon: item.lon } : undefined,
        };
      }),
    [items, seenAt, context],
  );
}
